import {
	AccommodationIcon,
	CloseIcon,
	ExpenseIcon,
	FileIcon,
	FlightIcon,
	LightbulbIcon,
	LinkIcon,
	PersonIcon,
	SwapIcon,
} from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { Button } from '@components/Button'
import { CATEGORY_GLYPHS } from '@components/CategoryField'
import type { EventCategory } from '@orbit/api-types'
import React from 'react'
import {
	AttachedCard,
	CardDetail,
	CardText,
	CardTitle,
	CategoryPill,
	Facepile,
	FacepileAvatar,
	IconThumbnail,
	IconTile,
	Leg,
	LegRoute,
	StreamCard,
	StreamCardButton,
} from './ChatCard.styles'
import { cardRoute, presentCard, type ChatCard, type ChatCardGlyph } from './core'

const GLYPHS: Record<ChatCardGlyph, React.ElementType> = {
	flight: FlightIcon,
	accommodation: AccommodationIcon,
	idea: LightbulbIcon,
	expense: ExpenseIcon,
	payUp: SwapIcon,
	member: PersonIcon,
	link: LinkIcon,
	unknown: FileIcon,
}

const MAX_FACES = 6

interface ChatGlyphProps {
	glyph: ChatCardGlyph
	category?: EventCategory | null
	fontSize: number
}

/** A card's glyph: its category's, for an activity or idea, else its kind's. */
export const ChatGlyph: React.FC<ChatGlyphProps> = ({ glyph, category, fontSize }) => {
	const Glyph = category ? CATEGORY_GLYPHS[category] : GLYPHS[glyph]
	return <Glyph sx={{ fontSize }} aria-hidden />
}

interface ChatCardIconProps {
	glyph: ChatCardGlyph
	category?: EventCategory | null
	thumbnail?: string | null
	size: number
	dimmed?: boolean
}

/** A card's leading tile: its thumbnail when it has one, else its glyph on a soft orange square. */
export const ChatCardIcon: React.FC<ChatCardIconProps> = ({ glyph, category, thumbnail, size, dimmed }) => (
	<IconTile $size={size} $dimmed={dimmed} aria-hidden>
		<ChatGlyph glyph={glyph} category={category} fontSize={size >= 40 ? iconSizes.lg : iconSizes.md} />
		{thumbnail && <IconThumbnail src={thumbnail} alt="" loading="lazy" referrerPolicy="no-referrer" />}
	</IconTile>
)

interface StreamChatCardProps {
	card: ChatCard
	/** Opens what it tags; a removed card, or a member, opens nothing. */
	onOpen?: (card: ChatCard) => void
}

/**
 * A tagged resource as the stream shows it: its icon, title and key facts, every leg of
 * a booking and the people on it. Clicking opens the resource; a removed one is greyed
 * and says what it was. Ported from iOS `ChatCardView`'s stream style.
 */
export const StreamChatCard: React.FC<StreamChatCardProps> = ({ card, onOpen }) => {
	const presentation = presentCard(card)
	const opens = onOpen && cardRoute(card) !== null
	const showsPeople = (card.kind === 'FlightGroup' || card.kind === 'Accommodation') && card.people.length > 0

	const content = (
		<>
			<ChatCardIcon
				glyph={presentation.glyph}
				category={presentation.category}
				thumbnail={card.thumbnail}
				size={36}
				dimmed={card.removed}
			/>
			<CardText $removed={card.removed}>
				<CardTitle $removed={card.removed}>{presentation.title}</CardTitle>
				{presentation.pill && <CategoryPill $removed={card.removed}>{presentation.pill}</CategoryPill>}
				{presentation.legs.map((leg, index) => (
					<Leg key={index}>
						<LegRoute>{leg.route}</LegRoute>
						{leg.times && <CardDetail>{leg.times}</CardDetail>}
					</Leg>
				))}
				{presentation.detail && <CardDetail $lines={2}>{presentation.detail}</CardDetail>}
				{showsPeople && (
					<Facepile>
						{card.people.slice(0, MAX_FACES).map(person => (
							<FacepileAvatar key={person.id} user={person} size="sm" ring title={person.firstName} />
						))}
						{card.people.length > MAX_FACES && (
							<FacepileAvatar size="sm" ring tone="gold" label={`+${card.people.length - MAX_FACES}`} />
						)}
					</Facepile>
				)}
			</CardText>
		</>
	)

	if (opens) {
		return (
			<StreamCardButton type="button" $removed={false} onClick={() => onOpen(card)} aria-label={`Open ${card.title}`}>
				{content}
			</StreamCardButton>
		)
	}
	return (
		<StreamCard $removed={card.removed} title={presentation.removedNotice ?? undefined}>
			{content}
		</StreamCard>
	)
}

interface AttachedChatCardProps {
	card: ChatCard
	onRemove: (card: ChatCard) => void
}

/** A card attached to the Message being written: compact, with a button that takes it off. */
export const AttachedChatCard: React.FC<AttachedChatCardProps> = ({ card, onRemove }) => {
	const presentation = presentCard(card)
	const [firstLeg] = presentation.legs
	const detail = presentation.detail ?? (firstLeg ? (firstLeg.times ?? firstLeg.route) : null)
	return (
		<AttachedCard $removed={false}>
			<ChatCardIcon glyph={presentation.glyph} category={presentation.category} thumbnail={card.thumbnail} size={32} />
			<CardText>
				<CardTitle $compact>{presentation.title}</CardTitle>
				{detail && <CardDetail>{detail}</CardDetail>}
			</CardText>
			<Button type="button" variant="icon" onClick={() => onRemove(card)} aria-label={`Remove ${card.title}`}>
				<CloseIcon sx={{ fontSize: iconSizes.sm }} />
			</Button>
		</AttachedCard>
	)
}
